import styles from './OptionList.module.css';

/** One choice: `value` is what the caller gets back, `label` is what is shown and searched, `detail` (a count, say) is shown after the label but not searched. */
export interface SelectOption {
  value: string;
  label: string;
  detail?: string;
}

interface OptionListProps {
  /** The options to show, already filtered. */
  options: SelectOption[];
  /** The values that are chosen. */
  picked: string[];
  /** Index of the option the keyboard or pointer is on. */
  activeIndex: number;
  /** Identifies the list, so the search box can point at it and at its active option. */
  listId: string;
  /** Show a checkbox on every option instead of a tick on the chosen one. */
  multiple?: boolean;
  onSelect: (value: string) => void;
  onHover: (index: number) => void;
}

/** The id of an option in the list, for aria-activedescendant. */
export const optionIdFor = (listId: string, index: number): string => `${listId}-option-${index}`;

const iconFor = (multiple: boolean, selected: boolean): string | null => {
  if (multiple) return selected ? 'check_box' : 'check_box_outline_blank';
  return selected ? 'check' : null;
};

// A list of options where some are chosen: one at a time (a tick) or several (checkboxes). It only draws; the parent decides what a click means.
export const OptionList = ({ options, picked, activeIndex, listId, multiple = false, onSelect, onHover }: OptionListProps) => (
  <div className={styles.list} id={listId} role="listbox" aria-multiselectable={multiple || undefined}>
    {options.map((option, index) => {
      const selected = picked.includes(option.value);
      const icon = iconFor(multiple, selected);
      return (
        <div
          key={option.value}
          id={optionIdFor(listId, index)}
          role="option"
          aria-selected={selected}
          className={`${styles.option} ${selected ? styles.optionSelected : ''} ${index === activeIndex ? styles.optionActive : ''}`}
          onClick={() => onSelect(option.value)}
          onMouseEnter={() => onHover(index)}
        >
          {icon && (
            <span className={`material-icons ${styles.checkIcon}`} aria-hidden="true">
              {icon}
            </span>
          )}
          {option.detail ? `${option.label} (${option.detail})` : option.label}
        </div>
      );
    })}
  </div>
);
